/**
 * Service de gestion des sondages en attente de sauvegarde
 *
 * Sauvegarde temporairement les sondages dans sessionStorage avant connexion
 * pour les restaurer après authentification.
 */

import { PollData } from "../hooks/usePolls";

const STORAGE_KEY = "doodates_pending_poll";
const EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24 heures

export interface PendingPollData {
  pollData: PollData;
  timestamp: number;
  returnPath: string;
  pollType: "date" | "form" | "quizz" | "availability";
}

export class PendingPollService {
  /**
   * Sauvegarder un sondage en attente avant connexion
   */
  static savePendingPoll(data: PendingPollData): void {
    try {
      const serialized = JSON.stringify(data);
      sessionStorage.setItem(STORAGE_KEY, serialized);
    } catch (error) {
      console.error(
        "❌ [PendingPollService] Failed to save pending poll",
        error,
      );
    }
  }

  /**
   * Récupérer un sondage en attente après connexion
   * Retourne null si aucun sondage ou si expiré
   */
  static getPendingPoll(): PendingPollData | null {
    try {
      const serialized = sessionStorage.getItem(STORAGE_KEY);
      if (!serialized) {
        return null;
      }

      const data: PendingPollData = JSON.parse(serialized);

      // Vérifier expiration (24h)
      const now = Date.now();
      const age = now - data.timestamp;
      if (age > EXPIRATION_MS) {
        console.log("⏰ [PendingPollService] Pending poll expired, clearing");
        this.clearPendingPoll();
        return null;
      }

      return data;
    } catch (error) {
      console.error(
        "❌ [PendingPollService] Failed to retrieve pending poll",
        error,
      );
      this.clearPendingPoll();
      return null;
    }
  }

  /**
   * Nettoyer le sondage en attente
   */
  static clearPendingPoll(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error(
        "❌ [PendingPollService] Failed to clear pending poll",
        error,
      );
    }
  }

  /**
   * Vérifier si un sondage en attente existe (non expiré)
   */
  static hasPendingPoll(): boolean {
    const poll = this.getPendingPoll();
    return poll !== null;
  }

  /**
   * Obtenir l'âge du sondage en attente en minutes
   */
  static getPendingPollAge(): number | null {
    const poll = this.getPendingPoll();
    if (!poll) return null;

    const ageMs = Date.now() - poll.timestamp;
    return Math.floor(ageMs / (60 * 1000));
  }
}
